'use memo';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuthStore } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { SHORTCUT_DEFINITIONS } from '../registry/definitions';
import type { ShortcutScope } from '../types';
import { ShortcutLabel } from './ShortcutLabel';
type RegistryShortcut = (typeof SHORTCUT_DEFINITIONS)[number];
const SCOPE_ORDER: ShortcutScope[] = ['global', 'launcher', 'session'];
const SCOPE_LABEL_KEY: Record<ShortcutScope, string> = {
  global: 'features.shortcuts.list.scopeGlobal',
  launcher: 'features.shortcuts.list.scopeLauncher',
  session: 'features.shortcuts.list.scopeSession'
};

// `as const satisfies` で各定義はリテラル型に絞られ、developer / featureFlag を持たない
// メンバーがあるため、プロパティ存在を `in` でナローイングしてから参照する。
const isDeveloper = (definition: RegistryShortcut): boolean => 'developer' in definition && definition.developer === true;
type ShortcutListDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};
type SectionProps = {
  titleKey: string;
  definitions: readonly RegistryShortcut[];
};
function ShortcutSection({
  titleKey,
  definitions
}: SectionProps) {
  const {
    t
  } = useTranslation(defaultNS);
  if (definitions.length === 0) return null;
  return <div>
      <div className={cn('text-labelSmall', 'mb-2 uppercase tracking-[0.06em] text-neutral-muted-on-fill')}>
        {t(titleKey)}
      </div>
      <ul className="flex flex-col">
        {definitions.map(definition => <li key={definition.id} className="flex items-center justify-between gap-4 border-b border-neutral-muted-border py-2 last:border-b-0">
            <span className={cn('text-labelReg', 'text-neutral-on-fill')}>
              {t(definition.labelKey)}
            </span>
            <ShortcutLabel shortcutId={definition.id} />
          </li>)}
      </ul>
    </div>;
}

/**
 * ショートカット一覧（⌘/ で開く。registry = SSOT をそのまま描画）。
 * 一覧は表示専用（ユーザーによるキー再割当は行わない）。
 * featureFlag 付き定義は該当フラグが有効なセッションでのみ表示し、
 * developer 付きは末尾の開発者ツールセクションに分けて表示する。
 */
export function ShortcutListDialog({
  open,
  onOpenChange
}: ShortcutListDialogProps) {
  const {
    t
  } = useTranslation(defaultNS);
  // 非リアクティブな featureFlags.check ではなく auth ストアを購読（リロード直後の再取得に追従）
  const featureFlags = useAuthStore(state => state.session?.featureFlags);
  const isVisible = (definition: RegistryShortcut): boolean => !('featureFlag' in definition) || Boolean(featureFlags?.[definition.featureFlag]);
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('features.shortcuts.list.title')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          {SCOPE_ORDER.map(scope => <ShortcutSection key={scope} titleKey={SCOPE_LABEL_KEY[scope]} definitions={SHORTCUT_DEFINITIONS.filter(definition => definition.scope === scope && !isDeveloper(definition) && isVisible(definition))} />)}
          <ShortcutSection titleKey="features.shortcuts.list.developerTools" definitions={SHORTCUT_DEFINITIONS.filter(definition => isDeveloper(definition) && isVisible(definition))} />
        </div>
      </DialogContent>
    </Dialog>;
}